import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const CURRENT_SYSTEM_VERSION = 'v2.4-2026-07-30';

function AtualizacaoSistemaModal({ externalIsOpen, externalOnClose, onOpenGuiaRapido }) {
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [hasUnread, setHasUnread] = useState(false);
    const location = useLocation();

    const isControlled = externalIsOpen !== undefined;
    const isOpen = isControlled ? externalIsOpen : internalIsOpen;

    useEffect(() => {
        const token = localStorage.getItem('token');
        setIsLoggedIn(!!token);

        const lastSeenVersion = localStorage.getItem('sigo_versao_lida');
        if (lastSeenVersion !== CURRENT_SYSTEM_VERSION) {
            setHasUnread(true);
            if (!isControlled) {
                setInternalIsOpen(true);
            }
        } else {
            setHasUnread(false);
        }
    }, [location, isControlled]);

    const handleClose = () => {
        localStorage.setItem('sigo_versao_lida', CURRENT_SYSTEM_VERSION);
        setHasUnread(false);
        if (externalOnClose) {
            externalOnClose();
        } else {
            setInternalIsOpen(false);
        }
    };

    const handleOpenModal = () => {
        localStorage.setItem('sigo_versao_lida', CURRENT_SYSTEM_VERSION);
        setHasUnread(false);
        setInternalIsOpen(true);
    };

    const handleAbrirGuia = () => {
        handleClose();
        if (onOpenGuiaRapido) {
            onOpenGuiaRapido();
        } else {
            const btnHelp = document.querySelector('button[title="Central de Ajuda / Guia Rápido"]');
            if (btnHelp) btnHelp.click();
        }
    };

    if (!isLoggedIn) return null;

    return (
        <>
            {/* BOTÃO FLUTUANTE CIRCULAR DE NOVIDADES (✨) */}
            {/* Posicionado diretamente acima do botão azul (bottom-22 vs bottom-6) com tamanho equilibrado (w-13 h-13) */}
            <button
                onClick={handleOpenModal}
                className="fixed bottom-24 right-6 z-[9999] bg-gradient-to-tr from-amber-400 via-orange-500 to-amber-600 text-white w-10 h-10 rounded-full shadow-2xl flex items-center justify-center hover:scale-110 active:scale-95 transition-all border-2 border-white ring-4 ring-amber-400/30 group cursor-pointer"
                title="✨ Novidades e Atualizações do Sistema (v2.4.0)"
            >
                {/* Badge de Notificação Pulsante quando houver novidade não lida */}
                {hasUnread && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-4 w-4 bg-red-600 border-2 border-white"></span>
                    </span>
                )}

                {/* Ícone SVG de Brilho/Estrela em Branco de Alta Definição */}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-white drop-shadow-md group-hover:rotate-12 transition-transform">
                    <path fillRule="evenodd" d="M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.93L8.279 5.084A.75.75 0 0 1 9 4.5ZM18 1.5a.75.75 0 0 1 .728.568l.258 1.036c.236.94.978 1.682 1.918 1.918l1.036.258a.75.75 0 0 1 0 1.456l-1.036.258a2.75 2.75 0 0 0-1.918 1.918l-.258 1.036a.75.75 0 0 1-1.456 0l-.258-1.036a2.75 2.75 0 0 0-1.918-1.918l-1.036-.258a.75.75 0 0 1 0-1.456l1.036-.258a2.75 2.75 0 0 0 1.918-1.918l.258-1.036A.75.75 0 0 1 18 1.5ZM16.5 15a.75.75 0 0 1 .712.513l.394 1.183c.15.447.5.799.948.948l1.183.395a.75.75 0 0 1 0 1.422l-1.183.395a1.75 1.75 0 0 0-.948.948l-.395 1.183a.75.75 0 0 1-1.422 0l-.395-1.183a1.75 1.75 0 0 0-.948-.948l-1.183-.395a.75.75 0 0 1 0-1.422l1.183-.395a1.75 1.75 0 0 0 .948-.948l.395-1.183A.75.75 0 0 1 16.5 15Z" clipRule="evenodd" />
                </svg>
            </button>

            {/* MODAL DE NOVIDADES & RELEASE NOTES */}
            {isOpen && (
                <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full overflow-hidden border border-gray-100 flex flex-col max-h-[90vh]">

                        {/* Header com Gradiente Sabesp & Destaque Amarelo */}
                        <div className="bg-gradient-to-r from-sabesp-blue via-blue-900 to-slate-900 p-6 text-white relative">
                            <div className="flex justify-between items-start">
                                <div className="space-y-1">
                                    <span className="px-3 py-1 bg-amber-500/90 text-white rounded-full text-[10px] font-extrabold uppercase tracking-widest inline-block shadow-sm">
                                        ✨ Atualização do Sistema
                                    </span>
                                    <h2 className="text-xl font-black text-white tracking-tight pt-1">
                                        O que Há de Novo no SIGO Sabesp
                                    </h2>
                                    <p className="text-xs text-blue-200 font-medium flex items-center gap-2">
                                        <span>📅 Lançado em: <strong>30/07/2026 às 14:40h</strong></span>
                                        <span>•</span>
                                        <span className="font-mono text-amber-300 font-bold">Versão 2.4.0</span>
                                    </p>
                                </div>
                                <button
                                    onClick={handleClose}
                                    className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center font-bold text-sm transition-colors"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        {/* Conteúdo com scroll */}
                        <div className="p-6 overflow-y-auto scrollbar-thin text-xs text-slate-700 space-y-4 flex-1">
                            <div className="bg-amber-50 border border-amber-200 p-3.5 rounded-2xl flex items-center gap-3 text-amber-900 font-semibold shadow-xs">
                                <span className="text-xl">🚀</span>
                                <span>Novos recursos de gestão, auditoria e edição de planejamento foram implantados com sucesso!</span>
                            </div>

                            <div className="space-y-3">
                                <h3 className="font-bold text-slate-800 text-xs uppercase tracking-wider border-b border-gray-100 pb-1.5 flex items-center gap-1.5">
                                    <span>📌</span> Principais Funcionalidades Adicionadas:
                                </h3>

                                {/* Item 1 */}
                                <div className="bg-slate-50 p-3.5 rounded-2xl border border-gray-100 space-y-1">
                                    <div className="font-extrabold text-sabesp-blue text-xs flex items-center gap-1.5">
                                        <span>📜</span> Registro de Logs de Auditoria & Exportação em PDF
                                    </div>
                                    <p className="text-[11px] text-gray-600 leading-relaxed">
                                        Aba de auditoria no modal de Configurações com busca histórica de todas as alterações feitas no sistema (quem alterou, quando e de/para) com filtros por usuário e período, permitindo emitir relatório oficial em <strong>PDF A4 Paisagem</strong>.
                                    </p>
                                </div>

                                {/* Item 2 */}
                                <div className="bg-slate-50 p-3.5 rounded-2xl border border-gray-100 space-y-1">
                                    <div className="font-extrabold text-red-600 text-xs flex items-center gap-1.5">
                                        <span>🗑️</span> Exclusão de Anos no Planejamento
                                    </div>
                                    <p className="text-[11px] text-gray-600 leading-relaxed">
                                        Botão de lixeira no cabeçalho das colunas de cada ano na tabela de economias, com confirmação de segurança e <strong>gravação detalhada nos logs</strong> de cada número, mês e status removidos.
                                    </p>
                                </div>

                                {/* Item 3 */}
                                <div className="bg-slate-50 p-3.5 rounded-2xl border border-gray-100 space-y-1">
                                    <div className="font-extrabold text-emerald-700 text-xs flex items-center gap-1.5">
                                        <span>📊</span> Detalhamento Mês a Mês x Acumulado Automático
                                    </div>
                                    <p className="text-[11px] text-gray-600 leading-relaxed">
                                        Sanfona de anos com expansão (+) e recolhimento (-). Quando você preenche os meses (jan a dez), o valor anual fechado passa a ser a <strong>soma exata dos meses preenchidos</strong>.
                                    </p>
                                </div>

                                {/* Item 4 */}
                                <div className="bg-slate-50 p-3.5 rounded-2xl border border-gray-100 space-y-1">
                                    <div className="font-extrabold text-blue-700 text-xs flex items-center gap-1.5">
                                        <span>📍</span> Padronização Auditável de Locais
                                    </div>
                                    <p className="text-[11px] text-gray-600 leading-relaxed">
                                        Todos os registros de ações e alterações agora identificam a obra no padrão oficial: <code className="font-mono bg-blue-100 text-sabesp-blue px-1.5 py-0.5 rounded text-[10px] font-bold">Obra ETE Barueri (ID 15)</code>.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Rodapé com Botões de Ação */}
                        <div className="bg-slate-50 px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
                            <button
                                onClick={handleAbrirGuia}
                                className="w-full sm:w-auto px-4 py-2.5 bg-blue-50 hover:bg-blue-100 text-sabesp-blue font-bold rounded-xl text-xs transition-all border border-blue-200 flex items-center justify-center gap-1.5 cursor-pointer"
                            >
                                <span>📖</span> Abrir Guia Rápido Completo
                            </button>
                            <button
                                onClick={handleClose}
                                className="w-full sm:w-auto px-6 py-2.5 bg-sabesp-blue hover:bg-blue-900 text-white font-bold rounded-xl text-xs transition-all shadow-md flex items-center justify-center gap-1.5 cursor-pointer"
                            >
                                <span>✅</span> Entendi, Acessar Sistema
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default AtualizacaoSistemaModal;
